// apps/web/src/components/OtpVerifyPanel.tsx
//
// Shared OTP verification panel used by both /signin and /signup.
// The submit button is enabled only when all 6 digits are filled
// (per phase-1-5 §4, the input auto-submits — but a manual submit
// is also available for keyboard / screen-reader users).

import { useEffect, useRef, useState } from 'react';
import { OtpInput } from './OtpInput.js';
import { Alert } from './ui/Alert.js';
import { Button } from './ui/Button.js';
import { Countdown } from './ui/Countdown.js';
import { apiPost, ApiError } from '../lib/apiClient.js';
import type { OtpSession } from '../lib/api.js';

export type OtpMode = 'signin' | 'signup';

export interface OtpVerifyPanelProps {
  mode: OtpMode;
  sessionId: string;
  contact: { type: 'email' | 'phone'; value: string };
  /** Called after a successful verify. */
  onSuccess: (data: { accessTokenExpiresIn: number; refreshTokenExpiresIn: number; receiptId?: string }) => void;
  /** "Switch contact" link. */
  onSwitchContact: () => void;
  /** Called when the OTP becomes invalid (locked / expired) and the user must restart the flow. */
  onRestart: () => void;
}

interface OtpVerifyResponse {
  verified: boolean;
  receiptId?: string;
  accessTokenExpiresIn?: number;
  refreshTokenExpiresIn?: number;
}

export function OtpVerifyPanel({
  mode,
  sessionId,
  contact,
  onSuccess,
  onSwitchContact,
  onRestart,
}: OtpVerifyPanelProps): JSX.Element {
  const [code, setCode] = useState('');
  const [error, setError] = useState<{ message: string; requestId: string | null } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const submitRef = useRef<() => void>(() => {});

  // Resend has a 45s cooldown per the backend's RESEND_COOLDOWN_MS.
  // Track when the last resend was sent so the button can show
  // a countdown.
  const [resendPending, setResendPending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendNotice, setResendNotice] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (code.length !== 6) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await apiPost<OtpVerifyResponse>('/api/v1/otp/verify', {
        sessionId,
        code,
      });
      if (res.data.verified) {
        onSuccess({
          accessTokenExpiresIn: res.data.accessTokenExpiresIn ?? 900,
          refreshTokenExpiresIn: res.data.refreshTokenExpiresIn ?? 604800,
          ...(res.data.receiptId ? { receiptId: res.data.receiptId } : {}),
        });
      } else {
        setError({ message: 'Verification failed. Try again.', requestId: res.requestId });
        setCode('');
      }
    } catch (err) {
      const apiErr = err as ApiError;
      // Map backend error codes to user-friendly copy. Per spec
      // §5 / §6 the verify handler collapses all "wrong code /
      // expired / unknown session / locked" cases to the same
      // generic copy — never reveal which digit was wrong.
      const code = apiErr?.code;
      if (code === 'OTP_EXPIRED' || code === 'OTP_LOCKED' || code === 'OTP_REPLAY') {
        setError({
          message: 'This code is no longer valid. We\'ll send you a new one — or you can switch contacts.',
          requestId: apiErr.requestId ?? null,
        });
      } else if (code === 'RATE_LIMITED' && apiErr.retryAfterSec != null) {
        setError({
          message: `Too many attempts. Try again in ${apiErr.retryAfterSec}s.`,
          requestId: apiErr.requestId ?? null,
        });
      } else {
        setError({
          message: apiErr?.message ?? 'Verification failed. Try again.',
          requestId: apiErr?.requestId ?? null,
        });
      }
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    submitRef.current = () => { void submit(); };
  });

  function onComplete(finalCode: string): void {
    setCode(finalCode);
    // Brief debounce so the user sees the last digit land before submit.
    setTimeout(() => submitRef.current(), 80);
  }

  async function onResend(): Promise<void> {
    setError(null);
    setResendNotice(null);
    setResendPending(true);
    try {
      // Signin uses /otp/resend; signup uses /auth/resend-signup.
      // Both accept the same body shape ({ sessionId, contact? }).
      const res = await apiPost<OtpSession>(
        mode === 'signup' ? '/api/v1/auth/resend-signup' : '/api/v1/otp/resend',
        mode === 'signup' ? { sessionId, contact } : { sessionId },
      );
      if (res.data.sessionId && res.data.expiresIn > 0) {
        setResendNotice('A new code was sent.');
        setResendCooldown(45);
        setCode('');
      } else {
        setResendNotice('A new code was sent.');
        setResendCooldown(45);
      }
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr?.code === 'RATE_LIMITED' && apiErr.retryAfterSec != null) {
        setResendCooldown(apiErr.retryAfterSec);
        setError({
          message: `Please wait ${apiErr.retryAfterSec}s before requesting another code.`,
          requestId: apiErr.requestId ?? null,
        });
      } else {
        setError({
          message: apiErr?.message ?? 'Could not resend code.',
          requestId: apiErr?.requestId ?? null,
        });
      }
    } finally {
      setResendPending(false);
    }
  }

  return (
    <div className="space-y-4">
      <OtpInput
        value={code}
        onChange={setCode}
        onComplete={onComplete}
        legend={`Enter the 6-digit code sent to your ${contact.type}`}
        hasError={!!error}
        disabled={submitting}
      />

      {error ? (
        <Alert variant="error" requestId={error.requestId}>
          {error.message}
          {error.message.includes('no longer valid') ? (
            <>
              {' '}
              <button
                type="button"
                className="underline"
                onClick={onRestart}
              >
                Restart
              </button>
              .
            </>
          ) : null}
        </Alert>
      ) : null}

      {resendNotice ? (
        <p className="text-xs text-gray-600 dark:text-gray-400">{resendNotice}</p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          onClick={() => void submit()}
          isLoading={submitting}
          disabled={code.length !== 6}
        >
          Verify
        </Button>
        <Button
          variant="ghost"
          onClick={() => void onResend()}
          isLoading={resendPending}
          disabled={resendCooldown > 0}
        >
          {resendCooldown > 0 ? (
            <>Resend in <Countdown seconds={resendCooldown} /></>
          ) : (
            'Resend code'
          )}
        </Button>
        <button
          type="button"
          onClick={onSwitchContact}
          className="ml-auto text-xs text-gray-500 underline hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          Use a different contact
        </button>
      </div>
    </div>
  );
}
